import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthInitializer } from '@/components/AuthInitializer';
import { GuestOnly, RouteGuard } from '@/components/RouteGuard';
import { AppShell } from '@/components/AppShell';
import { AdminShell } from '@/components/AdminShell';
import { EnvBadge } from '@/components/EnvBadge';
import { Toaster } from '@/components/Toaster';
import { useIdentity } from '@/stores/identity';
import SignUp from '@/pages/auth/SignUp';
import SignIn from '@/pages/auth/SignIn';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import AuthCallback from '@/pages/auth/AuthCallback';
import CheckEmail from '@/pages/auth/CheckEmail';
import Home from '@/pages/Home';
import Landing from '@/pages/Landing';
import FirstLocation from '@/pages/onboarding/FirstLocation';
import LocationsList from '@/pages/locations/LocationsList';
import LocationDetail from '@/pages/locations/LocationDetail';
import LocationHours from '@/pages/locations/LocationHours';
import StaffList from '@/pages/staff/StaffList';
import StaffNew from '@/pages/staff/StaffNew';
import StaffDetail from '@/pages/staff/StaffDetail';
import MyActivity from '@/pages/staff/MyActivity';
import NotificationPreferences from '@/pages/my-activity/NotificationPreferences';
import ManagerReports from '@/pages/manager/Reports';
import OwnerReports from '@/pages/owner/Reports';
import InviteAccept from '@/pages/invite/InviteAccept';
import ScheduleLanding from '@/pages/schedule/ScheduleLanding';
import ScheduleCalendar from '@/pages/schedule/ScheduleCalendar';
import ScheduleDay from '@/pages/schedule/ScheduleDay';
import TimeOffList from '@/pages/timeOff/TimeOffList';
import TimeOffNew from '@/pages/timeOff/TimeOffNew';
import TimeOffDetail from '@/pages/timeOff/TimeOffDetail';
import Audit from '@/pages/Audit';
import AdminOverview from '@/pages/admin/AdminOverview';
import OrgsList from '@/pages/admin/OrgsList';
import OrgDetail from '@/pages/admin/OrgDetail';
import SubscriptionsList from '@/pages/admin/SubscriptionsList';
import SubscriptionDetail from '@/pages/admin/SubscriptionDetail';
import PricingCatalog from '@/pages/admin/PricingCatalog';
import PromotionsList from '@/pages/admin/PromotionsList';
import PromotionEdit from '@/pages/admin/PromotionEdit';
import AdminAuditFeed from '@/pages/admin/AdminAuditFeed';
import AdminSettings from '@/pages/admin/AdminSettings';
import BannersList from '@/pages/admin/BannersList';
import BannerEdit from '@/pages/admin/BannerEdit';
import OwnerBannersList from '@/pages/owner/OwnerBannersList';
import OwnerBannerEdit from '@/pages/owner/OwnerBannerEdit';
import OwnerBannerRecipients from '@/pages/owner/OwnerBannerRecipients';
import OwnerCommsLog from '@/pages/owner/OwnerCommsLog';
import Privacy from '@/pages/legal/Privacy';
import Terms from '@/pages/legal/Terms';
import Dpa from '@/pages/legal/Dpa';
import Pricing from '@/pages/legal/Pricing';
import Subprocessors from '@/pages/legal/Subprocessors';
import Security from '@/pages/legal/Security';
import Accessibility from '@/pages/legal/Accessibility';
import Billing from '@/pages/Billing';

/**
 * Admin route helper — wraps a page in AppShell + AdminShell with the
 * system_admin role gate, so individual route lines stay readable.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteGuard allowedRoles={['system_admin']}>
      <AppShell>
        <AdminShell>{children}</AdminShell>
      </AppShell>
    </RouteGuard>
  );
}

/**
 * Root route resolver. Renders the public Landing page for guests and the
 * in-app Home dashboard (under AppShell) for authenticated users. The auth
 * status comes from the identity store, hydrated by AuthInitializer.
 *
 * 'unknown' / 'loading' fall through to RouteGuard's existing LoadingFallback
 * so we don't flash the marketing page during session resume on refresh.
 */
function RootRoute() {
  const status = useIdentity((s) => s.status);
  if (status === 'guest' || status === 'error') {
    return <Landing />;
  }
  return (
    <RouteGuard>
      <AppShell>
        <Home />
      </AppShell>
    </RouteGuard>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthInitializer>
        <EnvBadge />
        <Toaster />
        <Routes>
          <Route path="/auth/sign-up" element={<GuestOnly><SignUp /></GuestOnly>} />
          <Route path="/auth/sign-in" element={<GuestOnly><SignIn /></GuestOnly>} />
          <Route path="/auth/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
          <Route path="/auth/reset" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/check-email" element={<GuestOnly><CheckEmail /></GuestOnly>} />

          <Route path="/onboarding/first-location" element={
            <RouteGuard allowedRoles={['owner']}><FirstLocation /></RouteGuard>
          } />

          <Route path="/" element={<RootRoute />} />
          <Route path="/locations" element={<RouteGuard><AppShell><LocationsList /></AppShell></RouteGuard>} />
          <Route path="/locations/:id" element={<RouteGuard><AppShell><LocationDetail /></AppShell></RouteGuard>} />
          <Route path="/locations/:id/hours" element={
            <RouteGuard allowedRoles={['owner', 'manager']}><AppShell><LocationHours /></AppShell></RouteGuard>
          } />

          <Route path="/staff" element={
            <RouteGuard allowedRoles={['owner', 'manager']}><AppShell><StaffList /></AppShell></RouteGuard>
          } />
          <Route path="/staff/new" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><StaffNew /></AppShell></RouteGuard>
          } />
          <Route path="/staff/:id" element={
            <RouteGuard allowedRoles={['owner', 'manager']}><AppShell><StaffDetail /></AppShell></RouteGuard>
          } />
          <Route path="/my-activity" element={
            <RouteGuard allowedRoles={['staff', 'manager', 'owner']}><AppShell><MyActivity /></AppShell></RouteGuard>
          } />
          <Route path="/my-activity/notifications" element={
            <RouteGuard allowedRoles={['staff', 'manager', 'owner']}><AppShell><NotificationPreferences /></AppShell></RouteGuard>
          } />
          <Route path="/manager-reports" element={
            <RouteGuard allowedRoles={['manager', 'owner', 'system_admin']}><AppShell><ManagerReports /></AppShell></RouteGuard>
          } />
          <Route path="/owner-reports" element={
            <RouteGuard allowedRoles={['owner', 'system_admin']}><AppShell><OwnerReports /></AppShell></RouteGuard>
          } />
          <Route path="/invite/accept/:token" element={<InviteAccept />} />

          <Route path="/schedule" element={<RouteGuard><AppShell><ScheduleLanding /></AppShell></RouteGuard>} />
          <Route path="/schedule/:locationId/:yyyymm" element={
            <RouteGuard><AppShell><ScheduleCalendar /></AppShell></RouteGuard>
          } />
          <Route path="/schedule/:locationId/:yyyymm/day/:date" element={
            <RouteGuard><AppShell><ScheduleDay /></AppShell></RouteGuard>
          } />

          <Route path="/time-off" element={<RouteGuard><AppShell><TimeOffList /></AppShell></RouteGuard>} />
          <Route path="/time-off/new" element={<RouteGuard><AppShell><TimeOffNew /></AppShell></RouteGuard>} />
          <Route path="/time-off/:id" element={<RouteGuard><AppShell><TimeOffDetail /></AppShell></RouteGuard>} />

          <Route path="/audit" element={
            <RouteGuard allowedRoles={['owner', 'system_admin']}><AppShell><Audit /></AppShell></RouteGuard>
          } />

          {/* Admin cockpit — every section is wrapped in AdminShell + role gate */}
          <Route path="/admin"                              element={<AdminRoute><AdminOverview /></AdminRoute>} />
          <Route path="/admin/orgs"                         element={<AdminRoute><OrgsList /></AdminRoute>} />
          <Route path="/admin/orgs/:id"                     element={<AdminRoute><OrgDetail /></AdminRoute>} />
          <Route path="/admin/subscriptions"                element={<AdminRoute><SubscriptionsList /></AdminRoute>} />
          <Route path="/admin/subscriptions/:orgId"         element={<AdminRoute><SubscriptionDetail /></AdminRoute>} />
          <Route path="/admin/pricing"                      element={<AdminRoute><PricingCatalog /></AdminRoute>} />
          <Route path="/admin/promotions"                   element={<AdminRoute><PromotionsList /></AdminRoute>} />
          <Route path="/admin/promotions/new"               element={<AdminRoute><PromotionEdit /></AdminRoute>} />
          <Route path="/admin/banners"                      element={<AdminRoute><BannersList /></AdminRoute>} />
          <Route path="/admin/banners/new"                  element={<AdminRoute><BannerEdit /></AdminRoute>} />
          <Route path="/admin/banners/:id"                  element={<AdminRoute><BannerEdit /></AdminRoute>} />
          <Route path="/admin/audit"                        element={<AdminRoute><AdminAuditFeed /></AdminRoute>} />
          <Route path="/admin/settings"                     element={<AdminRoute><AdminSettings /></AdminRoute>} />

          <Route path="/billing" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><Billing /></AppShell></RouteGuard>
          } />
          <Route path="/owner/banners" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><OwnerBannersList /></AppShell></RouteGuard>
          } />
          <Route path="/owner/banners/new" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><OwnerBannerEdit /></AppShell></RouteGuard>
          } />
          <Route path="/owner/banners/:id" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><OwnerBannerEdit /></AppShell></RouteGuard>
          } />
          <Route path="/owner/banners/log" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><OwnerCommsLog /></AppShell></RouteGuard>
          } />
          <Route path="/owner/banners/:id/recipients" element={
            <RouteGuard allowedRoles={['owner']}><AppShell><OwnerBannerRecipients /></AppShell></RouteGuard>
          } />

          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/dpa" element={<Dpa />} />
          <Route path="/subprocessors" element={<Subprocessors />} />
          <Route path="/security" element={<Security />} />
          <Route path="/accessibility" element={<Accessibility />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthInitializer>
    </BrowserRouter>
  );
}
